import { Component, ChangeDetectionStrategy, input, viewChild, effect, signal } from '@angular/core';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';

export interface IStackedBarChartData {
  labels: string[];
  datasets: IStackedBarDataset[];
}

export interface IStackedBarDataset {
  label: string;
  data: number[];
  backgroundColor: string;
}

@Component({
  selector: 'app-stacked-bar-chart',
  templateUrl: './stacked-bar-chart.component.html',
  styleUrl: './stacked-bar-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StackedBarChartComponent {
  readonly chartData = input<IStackedBarChartData | null>(null);
  readonly xAxisLabel = input<string>();
  readonly yAxisLabel = input<string>();
  readonly showLegend = input(true);
  readonly hideYAxis = input(false);
  readonly roundedBars = input(false);
  readonly chart = viewChild<BaseChartDirective>(BaseChartDirective);

  readonly barChartData = signal<ChartData<'bar'> | null>(null);
  readonly barChartType = signal('bar' as const);
  readonly barChartOptions = signal<ChartOptions<'bar'>>(this.createChartOptions());
  readonly barChartPlugins = signal([DataLabelsPlugin]);

  constructor() {
    effect(
      () => {
        const data = this.chartData();
        this.updateChartData(data);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const hideY = this.hideYAxis();
        this.barChartOptions.set(this.createChartOptions(hideY));
        setTimeout(() => this.chart()?.update(), 100);
      },
      { allowSignalWrites: true }
    );
  }

  private updateChartData(chartData: IStackedBarChartData | null): void {
    if (!chartData) return;

    const useRoundedBars = this.roundedBars();
    const datasets: ChartDataset<'bar'>[] = chartData.datasets.map((dataset) => {
      const baseConfig = {
        label: dataset.label,
        data: dataset.data,
        backgroundColor: dataset.backgroundColor,
        borderWidth: 0,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      };

      if (useRoundedBars) {
        return {
          ...baseConfig,
          borderRadius: {
            topLeft: 4,
            topRight: 4,
            bottomLeft: 0,
            bottomRight: 0,
          },
          borderSkipped: false,
          inflateAmount: 1,
        };
      }

      return {
        ...baseConfig,
        borderRadius: 0,
      };
    });

    this.barChartData.set({
      labels: chartData.labels,
      datasets,
    });

    setTimeout(() => this.chart()?.update(), 100);
  }

  private createChartOptions(hideYAxis = false): ChartOptions<'bar'> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            color: '#696879',
            font: {
              size: 12,
              family: 'Inter',
            },
          },
          title: {
            display: !!this.xAxisLabel(),
            text: this.xAxisLabel(),
            color: '#696879',
            font: {
              size: 14,
              family: 'Inter',
            },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          display: !hideYAxis,
          grid: {
            display: !hideYAxis,
            color: '#E4E3E8',
            lineWidth: 1,
          },
          ticks: {
            display: !hideYAxis,
            color: '#696879',
            font: {
              size: 12,
              family: 'Inter',
            },
            stepSize: 2,
          },
          title: {
            display: !hideYAxis && !!this.yAxisLabel(),
            text: this.yAxisLabel(),
            color: '#696879',
            font: {
              size: 14,
              family: 'Inter',
            },
          },
        },
      },
      plugins: {
        legend: {
          display: this.showLegend(),
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 15,
            usePointStyle: false,
            font: {
              size: 12,
              family: 'Inter',
              weight: '400',
            },
            color: '#050320',
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: '#292929',
          titleColor: '#FFFFFF',
          bodyColor: '#FFFFFF',
          borderColor: '#292929',
          borderWidth: 1,
          cornerRadius: 4,
          padding: 8,
          displayColors: true,
          boxWidth: 12,
          boxHeight: 12,
          titleFont: {
            size: 12,
            family: 'Inter',
          },
          bodyFont: {
            size: 12,
            family: 'Inter',
          },
        },
        datalabels: {
          display: false,
        },
      },
    };
  }
}
